import createClient, { type Middleware } from "openapi-fetch";
import type { paths } from "./schema";
import { type ApiFailure, networkFailure, parseApiFailure } from "./errors";
import { notifyApiFailure } from "./failure-bus";
import type { TokenStore } from "../auth/token-store";

export type ApiClient = ReturnType<typeof createClient<paths>>;

export interface ApiClientOptions {
  /**
   * URL du back. Vient TOUJOURS de `VITE_API_BASE_URL`, jamais du contrat OpenAPI :
   * celui-ci embarque `servers: [http://localhost:8080]`, et un build prod qui s'y fierait
   * taperait localhost. Le snapshot gelé retire d'ailleurs ce champ.
   */
  readonly baseUrl: string;
  /** Si fourni, le jeton d'accès est injecté et renouvelé automatiquement. */
  readonly tokenStore?: TokenStore;
  /** Appelé quand la session est définitivement perdue (refresh refusé) — ex. router vers /login. */
  readonly onSessionExpired?: () => void;
}

const REFRESH_PATH = "/auth/refresh";
const LOGIN_PATH = "/auth/login";

/**
 * Construit le client HTTP typé du starter.
 *
 * Trois choses que le codegen ne peut PAS faire, et qui sont donc câblées ici :
 *
 * 1. **L'authentification.** Le contrat ne dit pas quelles routes attendent un Bearer
 *    (`securitySchemes` est déclaré, mais openapi-fetch ne l'applique pas). Un middleware
 *    pose le header dès qu'un jeton existe.
 * 2. **`credentials: "include"`.** Indispensable pour que le navigateur envoie le cookie
 *    `HttpOnly` du refresh token en cross-origin (dev : :5173 → :8080).
 * 3. **Le renouvellement sur 401** (ci-dessous) — transverse, comme le `TokenAuthenticator`
 *    du jumeau mobile. Ce n'est pas la responsabilité de `feature-auth` : n'importe quelle
 *    feature bénéficie du refresh, et il doit survivre au prune de l'écran de login.
 */
export function createApiClient({
  baseUrl,
  tokenStore,
  onSessionExpired,
}: ApiClientOptions): ApiClient {
  /**
   * Renouvellement **single-flight** : une seule requête de refresh à la fois.
   *
   * ⚠️ Ce n'est pas une optimisation, c'est une obligation. Le back applique une rotation
   * STRICTE : rejouer un refresh token déjà consommé renvoie 400 « expiré ou révoqué »
   * (mesuré). Si deux appels échouaient en 401 simultanément et déclenchaient chacun leur
   * refresh, le second invaliderait la session d'un utilisateur parfaitement légitime.
   * Ils partagent donc tous la même promesse.
   */
  let inflight: Promise<boolean> | null = null;

  async function performRefresh(): Promise<boolean> {
    // En mode cookie, `getRefreshToken()` renvoie null et un corps vide suffit : c'est le
    // navigateur qui porte le jeton (vérifié en live). On n'envoie un jeton dans le corps
    // que si on en détient déjà un — c'est-à-dire en mode de repli, sur un back qui ne
    // poserait pas le cookie.
    const stored = tokenStore?.getRefreshToken() ?? null;
    const cookieMode = stored === null;

    let response: Response;
    try {
      response = await fetch(`${baseUrl}${REFRESH_PATH}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(cookieMode ? {} : { refreshToken: stored }),
      });
    } catch {
      return false; // réseau coupé : ce n'est pas une session invalide, on ne déconnecte pas
    }

    if (!response.ok) return false;

    const tokens = (await response.json()) as { accessToken?: unknown; refreshToken?: unknown };
    if (typeof tokens.accessToken !== "string") return false;

    // ⚠️ En mode cookie, on NE conserve PAS le refresh token, bien que le back le renvoie
    // aussi dans le corps. Le back donne la priorité au corps sur le cookie : garder une
    // copie en mémoire reviendrait à envoyer NOTRE exemplaire, qui peut être périmé (un
    // autre onglet a pu faire tourner le jeton entre-temps), alors que le cookie, lui, est
    // toujours à jour. Le laisser au navigateur, c'est aussi le garder hors de portée du JS.
    tokenStore?.setTokens({
      accessToken: tokens.accessToken,
      refreshToken:
        !cookieMode && typeof tokens.refreshToken === "string" ? tokens.refreshToken : undefined,
    });
    return true;
  }

  /**
   * Sérialise le renouvellement **entre onglets** quand le navigateur le permet.
   *
   * `inflight` ne protège qu'un seul contexte JavaScript. Deux onglets ouverts sur l'app en
   * ont chacun un, et s'ignorent : ils peuvent donc lancer deux refresh simultanés, dont le
   * second se verrait refuser un jeton déjà consommé (rotation stricte du back).
   *
   * L'API Web Locks est faite exactement pour ça — un verrou nommé, partagé par toutes les
   * pages d'une même origine. Le second onglet attend, puis refresh à son tour avec le
   * cookie **déjà mis à jour** par le premier : il réussit au lieu d'échouer.
   *
   * Repli silencieux si l'API est absente : on retombe sur la protection par onglet, qui
   * couvre déjà le cas courant (plusieurs appels d'un même écran).
   */
  async function withCrossTabLock(run: () => Promise<boolean>): Promise<boolean> {
    if (typeof navigator === "undefined" || !("locks" in navigator)) return run();
    // `await` est nécessaire, pas décoratif : `request<T>` type T comme le retour BRUT du
    // callback, donc `Promise<Promise<boolean>>` ici. L'attente déplie les deux niveaux.
    return await navigator.locks.request(`starter-auth-refresh:${baseUrl}`, run);
  }

  function refreshSession(): Promise<boolean> {
    inflight ??= withCrossTabLock(performRefresh).finally(() => {
      inflight = null;
    });
    return inflight;
  }

  const fetchWithRefresh: typeof fetch = async (input, init) => {
    const request = input instanceof Request ? input : new Request(input, init);
    // Cloné AVANT l'envoi : le corps d'une Request ne se lit qu'une fois, il serait
    // inutilisable pour un second essai après consommation.
    const retryable = request.clone();

    const response = await fetch(request);

    if (response.status !== 401) return response;
    // Un 401 sur le refresh lui-même signifie que la session est morte : ne pas boucler.
    if (request.url.endsWith(REFRESH_PATH)) return response;
    // Un 401 sur la connexion veut dire « identifiants refusés », pas « jeton expiré » :
    // renouveler n'y changerait rien, coûterait une requête par mot de passe erroné, et
    // déclencherait `onSessionExpired` alors qu'aucune session n'existait.
    if (request.url.endsWith(LOGIN_PATH)) return response;
    if (!tokenStore) return response;

    const renewed = await refreshSession();
    if (!renewed) {
      tokenStore.clear();
      onSessionExpired?.();
      return response;
    }

    const headers = new Headers(retryable.headers);
    const token = tokenStore.getAccessToken();
    if (token !== null) headers.set("Authorization", `Bearer ${token}`);
    return fetch(new Request(retryable, { headers }));
  };

  const client = createClient<paths>({
    baseUrl,
    credentials: "include",
    fetch: fetchWithRefresh,
  });

  const auth: Middleware = {
    onRequest({ request }) {
      const token = tokenStore?.getAccessToken();
      if (token !== null && token !== undefined) {
        request.headers.set("Authorization", `Bearer ${token}`);
      }
      return request;
    },
  };

  client.use(auth);
  return client;
}

/** Résultat brut d'openapi-fetch : soit `data`, soit `error`, jamais les deux. */
interface FetchOutcome<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

/**
 * Convertit le triplet `{data, error, response}` d'openapi-fetch en valeur utilisable, ou
 * lève un `ApiFailure` normalisé.
 *
 * openapi-fetch ne lève jamais sur un statut d'erreur — il renseigne `error`. Pratique,
 * mais ça oblige chaque appelant à tester. Ce helper recentre la décision :
 *
 *   const health = unwrap(await client.GET("/health"));
 */
export function unwrap<T>(outcome: FetchOutcome<T>): T {
  const { data, error, response } = outcome;
  if (response.ok && data !== undefined) return data;
  throw parseApiFailure(response.status, error ?? null);
}

/**
 * Variante sans exception, pour les Reactive Controllers qui doivent peupler un état
 * `error` plutôt que laisser une exception remonter jusqu'au rendu.
 *
 *   const res = await attempt(() => client.GET("/health"));
 *   if (res.ok) this.data = res.value; else this.error = res.failure;
 */
export type Attempt<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly failure: ApiFailure };

export async function attempt<T>(call: () => Promise<FetchOutcome<T>>): Promise<Attempt<T>> {
  try {
    return { ok: true, value: unwrap(await call()) };
  } catch (cause) {
    // Soit un ApiFailure levé par unwrap, soit un échec réseau (fetch rejette sur coupure,
    // DNS, ou blocage CORS — sans réponse exploitable).
    const failure =
      typeof cause === "object" && cause !== null && "status" in cause
        ? (cause as ApiFailure)
        : networkFailure(cause);

    // Diffusé à qui veut l’écouter : certains codes concernent l’état de la session plutôt
    // que l’appel lui-même (EMAIL_NOT_VERIFIED, par exemple) et doivent pouvoir être
    // traités à un seul endroit. Voir `api/failure-bus.ts`.
    notifyApiFailure(failure);
    return { ok: false, failure };
  }
}
